import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import KpiBar from "@/components/KpiBar";
import PatientTable from "@/components/PatientTable";
import BatchBoard from "@/components/BatchBoard";
import CalendarView from "@/components/CalendarView";
import AddPatientDialog from "@/components/AddPatientDialog";
import OrthodontistManager from "@/components/OrthodontistManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Search, LogOut, ShieldCheck, Eye, Save, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const isMaster = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [threshold, setThreshold] = useState(2);
  const [thresholdInput, setThresholdInput] = useState(2);
  const [running, setRunning] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [search, setSearch] = useState("");
  const [batchFilter, setBatchFilter] = useState("all");
  const [kitFilter, setKitFilter] = useState("all");
  const [kits, setKits] = useState([]);
  const [orthoFilter, setOrthoFilter] = useState("all");
  const [orthodontists, setOrthodontists] = useState([]);
  const [flagged, setFlagged] = useState(false);
  const [sortBy, setSortBy] = useState("reg");
  const [months, setMonths] = useState([]);
  const [monthView, setMonthView] = useState("current");
  const [archive, setArchive] = useState(null);

  const loadMeta = useCallback(async () => {
    const [s, b, t, k, m, o] = await Promise.all([
      api.get("/stats"),
      api.get("/batches"),
      api.get("/settings/threshold"),
      api.get("/kits"),
      api.get("/months"),
      api.get("/orthodontists"),
    ]);
    setStats(s.data);
    setBatches(b.data);
    setThreshold(t.data.value);
    setThresholdInput(t.data.value);
    setKits(k.data);
    setMonths(m.data);
    setOrthodontists(o.data);
  }, []);

  const loadRunning = useCallback(async () => {
    const params = { status: "running" };
    if (search) params.search = search;
    if (batchFilter !== "all") params.batch = batchFilter;
    if (kitFilter !== "all") params.kit = kitFilter;
    if (orthoFilter !== "all") params.orthodontist = orthoFilter;
    if (flagged) params.flagged = true;
    const res = await api.get("/patients", { params });
    setRunning(res.data);
  }, [search, batchFilter, kitFilter, orthoFilter, flagged]);

  const loadCompleted = useCallback(async () => {
    const res = await api.get("/patients", { params: { status: "completed" } });
    setCompleted(res.data);
  }, []);

  const reload = useCallback(() => {
    loadMeta();
    loadRunning();
    loadCompleted();
  }, [loadMeta, loadRunning, loadCompleted]);

  useEffect(() => {
    loadMeta();
    loadCompleted();
  }, [loadMeta, loadCompleted]);

  useEffect(() => {
    loadRunning();
  }, [loadRunning]);

  useEffect(() => {
    if (monthView === "current") {
      setArchive(null);
      return;
    }
    api.get(`/months/${monthView}`).then((r) => setArchive(r.data)).catch(() => setArchive(null));
  }, [monthView]);

  const isArchive = monthView !== "current";
  const currentMonthLabel = (stats?.current_period_label || new Date().toLocaleDateString(undefined, { month: "long", year: "numeric" }));
  const activeBatches = isArchive ? (archive?.batches || []) : batches;
  const displayedRunning = useMemo(() => {
    let list = isArchive ? (archive?.patients || []) : running;
    if (isArchive && search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) =>
        [p.name, p.register_number, p.phone].some((v) => (v || "").toLowerCase().includes(q)));
    }
    const arr = [...list];
    if (sortBy === "name") arr.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortBy === "kit")
      arr.sort((a, b) => (a.braces_kit || "").localeCompare(b.braces_kit || "") || (a.name || "").localeCompare(b.name || ""));
    else
      arr.sort((a, b) => String(a.register_number || "").localeCompare(String(b.register_number || ""), undefined, { numeric: true }));
    return arr;
  }, [isArchive, archive, running, search, sortBy]);

  const saveThreshold = async () => {
    const val = Number(thresholdInput);
    if (!Number.isFinite(val) || val < 1) {
      toast.error("Threshold must be at least 1");
      return;
    }
    try {
      await api.put("/settings/threshold", { value: val });
      toast.success("Flag threshold updated");
      loadMeta();
      loadRunning();
    } catch (_) {
      toast.error("Failed to update threshold");
    }
  };

  const downloadRoster = async () => {
    try {
      const path = isArchive ? `/months/${monthView}/export` : "/patients/export";
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = isArchive
        ? `roster_${(archive?.label || "archive").replace(/[^a-z0-9]+/gi, "_")}.xlsx`
        : `roster_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isArchive ? `${archive?.label || "Archived"} roster downloaded` : "Roster downloaded");
    } catch (_) {
      toast.error("Failed to download roster");
    }
  };

  const deleteMonth = async () => {
    try {
      await api.delete(`/months/${monthView}`);
      toast.success("Archived month deleted");
      setMonthView("current");
      loadMeta();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/80 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl p-[2px] bg-gradient-to-br from-teal-300 to-emerald-500 shadow-sm shadow-teal-500/20">
              <div className="h-full w-full rounded-[9px] overflow-hidden bg-white">
                <img src="/tooth-logo.png" alt="Clinic logo" className="h-full w-full object-cover" />
              </div>
            </div>
            <div>
              <p className="font-heading font-bold text-sm leading-none text-slate-900">
                OrthoBatch
              </p>
              <p className="text-[11px] text-slate-500 mt-1">Orthodontic Appointment Manager</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge
              data-testid="role-indicator-badge"
              className={`rounded-full gap-1.5 border ${
                isMaster ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50" : "bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-100"
              }`}
            >
              {isMaster ? <ShieldCheck className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {isMaster ? "Master Admin" : "Client Staff"}
            </Badge>
            <span className="hidden sm:block text-sm text-slate-600">{user?.email}</span>
            <Button
              data-testid="logout-button"
              variant="outline"
              size="sm"
              onClick={logout}
              className="rounded-lg"
            >
              <LogOut className="h-4 w-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 animate-fade-up">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Appointment Roster
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Plan monthly batches, verify attendance and track missed check-ups.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <OrthodontistManager orthodontists={orthodontists} isMaster={isMaster} reload={reload} />
            {isMaster && (
              <Button
                data-testid="download-roster-button"
                variant="outline"
                onClick={downloadRoster}
                className="rounded-lg"
              >
                <Download className="h-4 w-4 mr-1.5" />
                {isArchive && archive?.label ? `Download ${archive.label}` : "Download Excel"}
              </Button>
            )}
            <AddPatientDialog batches={batches} orthodontists={orthodontists} reload={reload} />
          </div>
        </div>

        <KpiBar stats={stats} batches={batches} />

        <Tabs defaultValue="running" className="w-full">
          <TabsList className="bg-slate-100/80 border border-slate-200/80 rounded-xl p-1 h-auto gap-1 flex-wrap justify-start max-w-full">
            <TabsTrigger data-testid="tab-running-patients" value="running" className="rounded-lg px-4 py-2 font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-xs transition-all">
              Running Patients
            </TabsTrigger>
            <TabsTrigger data-testid="tab-batch-management" value="batches" className="rounded-lg px-4 py-2 font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-xs transition-all">
              Batch Management
            </TabsTrigger>
            <TabsTrigger data-testid="tab-calendar" value="calendar" className="rounded-lg px-4 py-2 font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-xs transition-all">
              Calendar
            </TabsTrigger>
            <TabsTrigger data-testid="tab-completed-patients" value="completed" className="rounded-lg px-4 py-2 font-medium text-slate-600 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-xs transition-all">
              Completed
            </TabsTrigger>
          </TabsList>

          <TabsContent value="running" className="mt-5 space-y-4">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
              <Select value={monthView} onValueChange={setMonthView}>
                <SelectTrigger data-testid="month-selector" className="w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="current">{currentMonthLabel} (Current)</SelectItem>
                  {months.map((m) => (
                    <SelectItem key={m.index} value={String(m.index)}>{m.label} · {m.patient_count}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="patient-search-input"
                  placeholder="Search name, reg no or phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger data-testid="sort-selector" className="w-[190px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="reg">Sort: Reg. Number</SelectItem>
                  <SelectItem value="name">Sort: Name (A–Z)</SelectItem>
                  <SelectItem value="kit">Sort: Braces Kit</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {isArchive ? (
              <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-2.5 text-sm text-violet-800" data-testid="archive-banner">
                Viewing archived roster for <strong>{archive?.label}</strong> — read-only snapshot.
              </div>
            ) : (
              <>
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  <Select value={batchFilter} onValueChange={setBatchFilter}>
                    <SelectTrigger data-testid="batch-filter" className="w-[170px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Batches</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {batches.map((b) => (
                        <SelectItem key={b.label} value={b.label}>Batch {b.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={kitFilter} onValueChange={setKitFilter}>
                    <SelectTrigger data-testid="kit-filter" className="w-[190px]">
                      <SelectValue placeholder="All Kits" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Braces Kits</SelectItem>
                      {kits.map((k) => (
                        <SelectItem key={k} value={k}>{k}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={orthoFilter} onValueChange={setOrthoFilter}>
                    <SelectTrigger data-testid="ortho-filter" className="w-[190px]">
                      <SelectValue placeholder="All Orthodontists" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All Orthodontists</SelectItem>
                      {orthodontists.map((o) => (
                        <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    data-testid="flag-filter-toggle"
                    variant={flagged ? "default" : "outline"}
                    onClick={() => setFlagged((f) => !f)}
                    className={`rounded-lg ${flagged ? "bg-rose-600 hover:bg-rose-700" : ""}`}
                  >
                    Frequent No-Show only
                  </Button>
                </div>

                <Card className="p-4 border-slate-200 flex flex-wrap items-center gap-3">
                  <span className="text-sm text-slate-600">Flag patients as frequent no-show after</span>
                  <Input
                    data-testid="threshold-input"
                    type="number"
                    min={1}
                    value={thresholdInput}
                    onChange={(e) => setThresholdInput(e.target.value)}
                    disabled={!isMaster}
                    className="w-20"
                  />
                  <span className="text-sm text-slate-600">missed appointments.</span>
                  {isMaster && (
                    <Button data-testid="threshold-save" size="sm" onClick={saveThreshold} variant="outline" className="rounded-lg">
                      <Save className="h-4 w-4 mr-1.5" /> Save
                    </Button>
                  )}
                  {!isMaster && (
                    <span className="text-xs text-slate-400">(Master admin can change this)</span>
                  )}
                </Card>
              </>
            )}

            <PatientTable
              patients={displayedRunning}
              batches={activeBatches}
              isMaster={isMaster}
              threshold={threshold}
              reload={reload}
              orthodontists={orthodontists}
              readOnly={isArchive}
            />
          </TabsContent>

          <TabsContent value="batches" className="mt-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Select value={monthView} onValueChange={setMonthView}>
                <SelectTrigger data-testid="month-selector-batches" className="w-[220px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="current">{currentMonthLabel} (Current)</SelectItem>
                  {months.map((m) => (
                    <SelectItem key={m.index} value={String(m.index)}>{m.label} · {m.patient_count}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isArchive && (
                <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm text-violet-800 flex-1">
                  Viewing archived batches for <strong>{archive?.label}</strong> — read-only snapshot.
                </div>
              )}
              {isArchive && isMaster && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="delete-month-button" variant="outline" className="rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50 shrink-0">
                      <Trash2 className="h-4 w-4 mr-1.5" /> Delete Month
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {archive?.label}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently removes the archived snapshot for this month. Your current patient records are not affected.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="delete-month-confirm" onClick={deleteMonth} className="bg-destructive hover:bg-destructive/90">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            <BatchBoard
              patients={isArchive ? (archive?.patients || []) : running}
              batches={activeBatches}
              isMaster={isMaster}
              reload={reload}
              readOnly={isArchive}
            />
          </TabsContent>

          <TabsContent value="calendar" className="mt-5">
            <CalendarView batches={batches} patients={running} orthodontists={orthodontists} />
          </TabsContent>

          <TabsContent value="completed" className="mt-5">
            <PatientTable
              patients={completed}
              batches={batches}
              isMaster={isMaster}
              threshold={threshold}
              reload={reload}
              orthodontists={orthodontists}
              completed
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
